import { HierarchicalKind } from '../../../../base/common/hierarchicalKind.js';
import { KeyMod as KeyMod$1, KeyCode } from '../../../../base/common/keyCodes.js';
import { escapeRegExpCharacters } from '../../../../base/common/strings.js';
import { EditorCommand, EditorAction } from '../../../browser/editorExtensions.js';
import { EditorContextKeys } from '../../../common/editorContextKeys.js';
import { codeActionCommandId, quickFixCommandId, refactorCommandId, sourceActionCommandId, organizeImportsCommandId, fixAllCommandId, autoFixCommandId } from './codeAction.js';
import { localizeWithPath } from '../../../../nls.js';
import { ContextKeyExpr } from '../../../../platform/contextkey/common/contextkey.js';
import { KeybindingWeight } from '../../../../platform/keybinding/common/keybindingsRegistry.js';
import { CodeActionCommandArgs, CodeActionAutoApply, CodeActionTriggerSource, CodeActionKind } from '../common/types.js';
import { CodeActionController } from './codeActionController.js';
import { SUPPORTED_CODE_ACTIONS } from './codeActionModel.js';
const _moduleId = "vs/editor/contrib/codeAction/browser/codeActionCommands";
function contextKeyForSupportedActions(kind) {
    return (
         (ContextKeyExpr.regex(
            ( (SUPPORTED_CODE_ACTIONS.keys()))[0],
             (new RegExp('(\\s|^)' + escapeRegExpCharacters(kind.value) + '\\b'))
        ))
    );
}
const argsSchema = {
    type: 'object',
    defaultSnippets: [{ body: { kind: '' } }],
    properties: {
        'kind': {
            type: 'string',
            description: ( localizeWithPath(_moduleId, 0, "Kind of the code action to run.")),
        },
        'apply': {
            type: 'string',
            description: ( localizeWithPath(_moduleId, 1, "Controls when the returned actions are applied.")),
            default: CodeActionAutoApply.IfSingle,
            enum: [CodeActionAutoApply.First, CodeActionAutoApply.IfSingle, CodeActionAutoApply.Never],
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 2, "Always apply the first returned code action.")),
                ( localizeWithPath(
                    _moduleId,
                    3,
                    "Apply the first returned code action if it is the only one."
                )),
                ( localizeWithPath(_moduleId, 4, "Do not apply the returned code actions.")),
            ]
        },
        'preferred': {
            type: 'boolean',
            default: false,
            description: ( localizeWithPath(
                _moduleId,
                5,
                "Controls if only preferred code actions should be returned."
            )),
        }
    }
};
function triggerCodeActionsForEditorSelection(editor, notAvailableMessage, filter, autoApply, triggerAction = CodeActionTriggerSource.Default) {
    if (editor.hasModel()) {
        const controller = CodeActionController.get(editor);
        controller?.manualTriggerAtCurrentPosition(notAvailableMessage, triggerAction, filter, autoApply);
    }
}
class QuickFixAction extends EditorAction {
    constructor() {
        super({
            id: quickFixCommandId,
            label: ( localizeWithPath(_moduleId, 6, "Quick Fix...")),
            alias: 'Quick Fix...',
            precondition: ( (ContextKeyExpr.and(EditorContextKeys.writable, EditorContextKeys.hasCodeActionsProvider))),
            kbOpts: {
                kbExpr: EditorContextKeys.textInputFocus,
                primary: KeyMod$1.CtrlCmd | KeyCode.Period,
                weight: KeybindingWeight.EditorContrib
            }
        });
    }
    run(_accessor, editor) {
        return triggerCodeActionsForEditorSelection(editor, ( localizeWithPath(_moduleId, 7, "No code actions available")), undefined, undefined, CodeActionTriggerSource.QuickFix);
    }
}
class CodeActionCommand extends EditorCommand {
    constructor() {
        super({
            id: codeActionCommandId,
            precondition: ( (ContextKeyExpr.and(EditorContextKeys.writable, EditorContextKeys.hasCodeActionsProvider))),
            metadata: {
                description: 'Trigger a code action',
                args: [{ name: 'args', schema: argsSchema, }]
            }
        });
    }
    runEditorCommand(_accessor, editor, userArgs) {
        const args = CodeActionCommandArgs.fromUser(userArgs, {
            kind: HierarchicalKind.Empty,
            apply: CodeActionAutoApply.IfSingle,
        });
        return triggerCodeActionsForEditorSelection(editor, typeof userArgs?.kind === 'string'
            ? args.preferred
                ? ( localizeWithPath(
            _moduleId,
            8,
            "No preferred code actions for '{0}' available",
            userArgs.kind
        ))
                : ( localizeWithPath(_moduleId, 9, "No code actions for '{0}' available", userArgs.kind))
            : args.preferred
                ? ( localizeWithPath(_moduleId, 10, "No preferred code actions available"))
                : ( localizeWithPath(_moduleId, 11, "No code actions available")), {
            include: args.kind,
            includeSourceActions: true,
            onlyIncludePreferredActions: args.preferred,
        }, args.apply);
    }
}
class RefactorAction extends EditorAction {
    constructor() {
        super({
            id: refactorCommandId,
            label: ( localizeWithPath(_moduleId, 12, "Refactor...")),
            alias: 'Refactor...',
            precondition: ( (ContextKeyExpr.and(EditorContextKeys.writable, EditorContextKeys.hasCodeActionsProvider))),
            kbOpts: {
                kbExpr: EditorContextKeys.textInputFocus,
                primary: KeyMod$1.CtrlCmd | KeyMod$1.Shift | KeyCode.KeyR,
                mac: {
                    primary: KeyMod$1.WinCtrl | KeyMod$1.Shift | KeyCode.KeyR
                },
                weight: KeybindingWeight.EditorContrib
            },
            contextMenuOpts: {
                group: '1_modification',
                order: 2,
                when: ( (ContextKeyExpr.and(
                    EditorContextKeys.writable,
                    contextKeyForSupportedActions(CodeActionKind.Refactor)
                ))),
            },
            metadata: {
                description: 'Refactor...',
                args: [{ name: 'args', schema: argsSchema }]
            }
        });
    }
    run(_accessor, editor, userArgs) {
        const args = CodeActionCommandArgs.fromUser(userArgs, {
            kind: CodeActionKind.Refactor,
            apply: CodeActionAutoApply.Never
        });
        return triggerCodeActionsForEditorSelection(editor, typeof userArgs?.kind === 'string'
            ? args.preferred
                ? ( localizeWithPath(
            _moduleId,
            13,
            "No preferred refactorings for '{0}' available",
            userArgs.kind
        ))
                : ( localizeWithPath(_moduleId, 14, "No refactorings for '{0}' available", userArgs.kind))
            : args.preferred
                ? ( localizeWithPath(_moduleId, 15, "No preferred refactorings available"))
                : ( localizeWithPath(_moduleId, 16, "No refactorings available")), {
            include: CodeActionKind.Refactor.contains(args.kind) ? args.kind : HierarchicalKind.None,
            onlyIncludePreferredActions: args.preferred
        }, args.apply, CodeActionTriggerSource.Refactor);
    }
}
class SourceAction extends EditorAction {
    constructor() {
        super({
            id: sourceActionCommandId,
            label: ( localizeWithPath(_moduleId, 17, "Source Action...")),
            alias: 'Source Action...',
            precondition: ( (ContextKeyExpr.and(EditorContextKeys.writable, EditorContextKeys.hasCodeActionsProvider))),
            contextMenuOpts: {
                group: '1_modification',
                order: 2.1,
                when: ( (ContextKeyExpr.and(
                    EditorContextKeys.writable,
                    contextKeyForSupportedActions(CodeActionKind.Source)
                ))),
            },
            metadata: {
                description: 'Source Action...',
                args: [{ name: 'args', schema: argsSchema }]
            }
        });
    }
    run(_accessor, editor, userArgs) {
        const args = CodeActionCommandArgs.fromUser(userArgs, {
            kind: CodeActionKind.Source,
            apply: CodeActionAutoApply.Never
        });
        return triggerCodeActionsForEditorSelection(editor, typeof userArgs?.kind === 'string'
            ? args.preferred
                ? ( localizeWithPath(
            _moduleId,
            18,
            "No preferred source actions for '{0}' available",
            userArgs.kind
        ))
                : ( localizeWithPath(_moduleId, 19, "No source actions for '{0}' available", userArgs.kind))
            : args.preferred
                ? ( localizeWithPath(_moduleId, 20, "No preferred source actions available"))
                : ( localizeWithPath(_moduleId, 21, "No source actions available")), {
            include: CodeActionKind.Source.contains(args.kind) ? args.kind : HierarchicalKind.None,
            includeSourceActions: true,
            onlyIncludePreferredActions: args.preferred,
        }, args.apply, CodeActionTriggerSource.SourceAction);
    }
}
class OrganizeImportsAction extends EditorAction {
    constructor() {
        super({
            id: organizeImportsCommandId,
            label: ( localizeWithPath(_moduleId, 22, "Organize Imports")),
            alias: 'Organize Imports',
            precondition: ( (ContextKeyExpr.and(
                EditorContextKeys.writable,
                contextKeyForSupportedActions(CodeActionKind.SourceOrganizeImports)
            ))),
            kbOpts: {
                kbExpr: EditorContextKeys.textInputFocus,
                primary: KeyMod$1.Shift | KeyMod$1.Alt | KeyCode.KeyO,
                weight: KeybindingWeight.EditorContrib
            },
        });
    }
    run(_accessor, editor) {
        return triggerCodeActionsForEditorSelection(editor, ( localizeWithPath(_moduleId, 23, "No organize imports action available")), { include: CodeActionKind.SourceOrganizeImports, includeSourceActions: true }, CodeActionAutoApply.IfSingle, CodeActionTriggerSource.OrganizeImports);
    }
}
class FixAllAction extends EditorAction {
    constructor() {
        super({
            id: fixAllCommandId,
            label: ( localizeWithPath(_moduleId, 24, "Fix All")),
            alias: 'Fix All',
            precondition: ( (ContextKeyExpr.and(
                EditorContextKeys.writable,
                contextKeyForSupportedActions(CodeActionKind.SourceFixAll)
            )))
        });
    }
    run(_accessor, editor) {
        return triggerCodeActionsForEditorSelection(editor, ( localizeWithPath(_moduleId, 25, "No fix all action available")), { include: CodeActionKind.SourceFixAll, includeSourceActions: true }, CodeActionAutoApply.IfSingle, CodeActionTriggerSource.FixAll);
    }
}
class AutoFixAction extends EditorAction {
    constructor() {
        super({
            id: autoFixCommandId,
            label: ( localizeWithPath(_moduleId, 26, "Auto Fix...")),
            alias: 'Auto Fix...',
            precondition: ( (ContextKeyExpr.and(
                EditorContextKeys.writable,
                contextKeyForSupportedActions(CodeActionKind.QuickFix)
            ))),
            kbOpts: {
                kbExpr: EditorContextKeys.textInputFocus,
                primary: KeyMod$1.Alt | KeyMod$1.Shift | KeyCode.Period,
                mac: {
                    primary: KeyMod$1.CtrlCmd | KeyMod$1.Alt | KeyCode.Period
                },
                weight: KeybindingWeight.EditorContrib
            }
        });
    }
    run(_accessor, editor) {
        return triggerCodeActionsForEditorSelection(editor, ( localizeWithPath(_moduleId, 27, "No auto fixes available")), {
            include: CodeActionKind.QuickFix,
            onlyIncludePreferredActions: true
        }, CodeActionAutoApply.IfSingle, CodeActionTriggerSource.AutoFix);
    }
}
export { AutoFixAction, CodeActionCommand, FixAllAction, OrganizeImportsAction, QuickFixAction, RefactorAction, SourceAction };
